import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  Tooltip,
  TextField,
  Snackbar,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import leaveApiService from "../../../services/leave-api";
import {
  Employee,
  LeaveBalance,
  LeaveRequest,
  LeaveRequestStatus,
} from "../../../types/leave";
import LeaveGrantDialog from "../../../components/LeaveGrantDialog";
import { formatDate } from "../../../types/leave";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const EmployeeLeaveCredits: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
  const [pendingSelectedEmployees, setPendingSelectedEmployees] = useState<
    Employee[]
  >([]);
  const [employeeBalances, setEmployeeBalances] = useState<
    Map<number, LeaveBalance[]>
  >(new Map());
  const [employeeLeaveRequests, setEmployeeLeaveRequests] = useState<
    Map<number, LeaveRequest[]>
  >(new Map());
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  const [leaveGrantDialogOpen, setLeaveGrantDialogOpen] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    requestId: number | null;
    employeeName: string;
    leaveDetails: string;
  }>({
    open: false,
    requestId: null,
    employeeName: "",
    leaveDetails: "",
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Year selector state
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Leave allocation states
  const [showLeaveAllocationDialog, setShowLeaveAllocationDialog] =
    useState(false);
  const [leaveAllocationYear, setLeaveAllocationYear] = useState(currentYear);
  const [processingLeaveAllocation, setProcessingLeaveAllocation] =
    useState(false);
  const [leaveAllocationMessage, setLeaveAllocationMessage] = useState<
    string | null
  >(null);

  // Load all employees on mount
  useEffect(() => {
    loadAllEmployees();
  }, []);

  // Load balances when confirmed selected employees or year changes
  useEffect(() => {
    if (selectedEmployees.length > 0) {
      loadBalancesForSelectedEmployees();
    } else {
      setEmployeeBalances(new Map());
    }
  }, [selectedEmployees, selectedYear]);

  const loadAllEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      // Load all employees without pagination (or with large page size)
      const employeeList = await leaveApiService.getAllEmployees(0, 1000);
      setAllEmployees(employeeList);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("error.failedToLoadEmployees"),
      );
    } finally {
      setLoading(false);
    }
  };

  const loadBalancesForSelectedEmployees = async () => {
    try {
      setLoadingBalances(true);
      const balancesMap = new Map<number, LeaveBalance[]>();
      const requestsMap = new Map<number, LeaveRequest[]>();

      for (const employee of selectedEmployees) {
        try {
          const balances = await leaveApiService.getEmployeeBalancesAdmin(
            employee.id,
            selectedYear,
          );
          balancesMap.set(employee.id, balances);

          // Also load leave requests for the selected year
          const requests = await leaveApiService.getEmployeeLeaveRequestsByYear(
            employee.id,
            selectedYear,
          );
          requestsMap.set(employee.id, requests);
        } catch {
          // Silently skip if balance loading fails for individual employee
        }
      }

      setEmployeeBalances(balancesMap);
      setEmployeeLeaveRequests(requestsMap);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("error.failedToLoadLeaveBalances"),
      );
    } finally {
      setLoadingBalances(false);
    }
  };

  // Sort employees by office location and group them
  const sortedEmployees = useMemo(() => {
    return [...allEmployees].sort((a, b) => {
      const locationA = a.officeLocation || t("common.noOfficeLocation");
      const locationB = b.officeLocation || t("common.noOfficeLocation");
      // Sort by location first, then by name
      if (locationA !== locationB) {
        return locationA.localeCompare(locationB);
      }
      return `${a.firstName} ${a.lastName}`.localeCompare(
        `${b.firstName} ${b.lastName}`,
      );
    });
  }, [allEmployees]);

  // Group employees by office location
  const employeesByLocation = useMemo(() => {
    const grouped = new Map<string, Employee[]>();
    sortedEmployees.forEach((emp) => {
      const location = emp.officeLocation || t("common.noOfficeLocation");
      if (!grouped.has(location)) {
        grouped.set(location, []);
      }
      grouped.get(location)!.push(emp);
    });
    return grouped;
  }, [sortedEmployees]);

  const handleGrantLeave = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setLeaveGrantDialogOpen(true);
  }, []);

  const handleLeaveGrantSuccess = useCallback(() => {
    setSnackbar({
      open: true,
      message: t("leave.messages.requestDenied"),
      severity: "success",
    });
    // Reload balances for selected employees
    loadBalancesForSelectedEmployees();
  }, [t, selectedEmployees, selectedYear]);

  const handleDeleteLeaveRequest = useCallback(
    (
      requestId: number,
      employeeName: string,
      leaveTypeName: string,
      startDate: string,
      endDate: string,
    ) => {
      setDeleteConfirmDialog({
        open: true,
        requestId,
        employeeName,
        leaveDetails: `${leaveTypeName}: ${formatDate(startDate)} - ${formatDate(endDate)}`,
      });
    },
    [],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmDialog.requestId) return;

    try {
      await leaveApiService.deleteLeaveRequest(deleteConfirmDialog.requestId);
      setSnackbar({
        open: true,
        message: t("leave.messages.leaveRequestDeleted"),
        severity: "success",
      });
      // Reload balances and requests
      loadBalancesForSelectedEmployees();
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error
            ? err.message
            : t("leave.messages.failedToDeleteLeaveRequest"),
        severity: "error",
      });
    } finally {
      setDeleteConfirmDialog({
        open: false,
        requestId: null,
        employeeName: "",
        leaveDetails: "",
      });
    }
  }, [deleteConfirmDialog.requestId, t]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmDialog({
      open: false,
      requestId: null,
      employeeName: "",
      leaveDetails: "",
    });
  }, []);

  const handleSelectAllFromLocation = useCallback(
    (location: string) => {
      const employeesInLocation = employeesByLocation.get(location) || [];
      // Replace current selection with employees from this location only
      setPendingSelectedEmployees(employeesInLocation);
      // Immediately confirm the selection and load balances
      setSelectedEmployees(employeesInLocation);
    },
    [employeesByLocation],
  );

  const handleClearSelection = useCallback(() => {
    setPendingSelectedEmployees([]);
    setSelectedEmployees([]);
  }, []);

  const handleDropdownClose = useCallback(() => {
    // When dropdown closes, confirm the pending selection
    setSelectedEmployees(pendingSelectedEmployees);
  }, [pendingSelectedEmployees]);

  const handleProcessLeaveAllocation = async () => {
    setProcessingLeaveAllocation(true);
    setLeaveAllocationMessage(null);
    try {
      const response = await fetch(
        `/ofc/leave/admin/process/full-annual/${leaveAllocationYear}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      setLeaveAllocationMessage(
        `Successfully processed full annual leave allocation for ${leaveAllocationYear}. All three steps completed: Annual Allocation, Accrual Increase, and Carry-Over.`,
      );
    } catch (err) {
      setLeaveAllocationMessage(
        `Error: ${err instanceof Error ? err.message : "Failed to process annual allocation"}`,
      );
    } finally {
      setProcessingLeaveAllocation(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 0, pr: 2, pb: 2, pl: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* Multi-select employee dropdown grouped by office location */}
        <Box sx={{ flex: 1, minWidth: 400 }}>
          <Autocomplete
            multiple
            options={sortedEmployees}
            value={pendingSelectedEmployees}
            onChange={(_, newValue) => setPendingSelectedEmployees(newValue)}
            onClose={handleDropdownClose}
            groupBy={(option) => option.officeLocation || "No Office Location"}
            getOptionLabel={(option) =>
              `${option.firstName} ${option.lastName} (${option.department || "N/A"})`
            }
            disableCloseOnSelect
            size="small"
            renderInput={(params) => (
              <TextField {...params} placeholder={t("leave.selectEmployees")} />
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.firstName} {option.lastName}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  ({option.department || "N/A"})
                </Typography>
              </li>
            )}
            renderGroup={(params) => (
              <li key={params.key}>
                <Box
                  sx={{
                    position: "sticky",
                    top: -8,
                    padding: "4px 10px",
                    bgcolor: "background.paper",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "primary.main",
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  {params.group}
                </Box>
                <ul style={{ padding: 0 }}>{params.children}</ul>
              </li>
            )}
          />
          {/* Quick selection buttons */}
          <Box
            sx={{
              mt: 1,
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {Array.from(employeesByLocation.keys()).map((location) => (
              <Button
                key={location}
                size="small"
                variant="outlined"
                onClick={() => handleSelectAllFromLocation(location)}
                sx={{ fontSize: "0.75rem", py: 0.5 }}
              >
                {t("leave.selectAll")} - {location} (
                {employeesByLocation.get(location)?.length || 0})
              </Button>
            ))}
            {(pendingSelectedEmployees.length > 0 ||
              selectedEmployees.length > 0) && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleClearSelection}
                sx={{ fontSize: "0.75rem", py: 0.5 }}
              >
                {t("common.clearAll")}
              </Button>
            )}
            {selectedEmployees.length > 0 && (
              <Chip
                label={`${selectedEmployees.length} ${t("leave.employeesSelected")}`}
                color="primary"
                size="small"
              />
            )}
          </Box>
        </Box>

        {/* Year Selector */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t("leave.year")}:
          </Typography>
          <ToggleButtonGroup
            value={selectedYear}
            exclusive
            onChange={(_, newYear) => {
              if (newYear !== null) {
                setSelectedYear(newYear);
              }
            }}
            size="small"
          >
            <ToggleButton value={currentYear - 1}>
              {currentYear - 1}
            </ToggleButton>
            <ToggleButton value={currentYear}>
              {currentYear}
              <Chip
                label={t("leave.current")}
                size="small"
                sx={{ ml: 1, height: 20 }}
              />
            </ToggleButton>
            <ToggleButton value={currentYear + 1}>
              {currentYear + 1}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {loadingBalances ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{t("leave.loadingBalances")}</Typography>
        </Box>
      ) : selectedEmployees.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">
            {t("leave.selectEmployeesToView")}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {selectedEmployees.map((employee) => (
            <EmployeeCard
              key={employee.id}
              employee={employee}
              balances={employeeBalances.get(employee.id) || []}
              leaveRequests={employeeLeaveRequests.get(employee.id) || []}
              year={selectedYear}
              currentYear={currentYear}
              theme={theme}
              t={t}
              onGrantLeave={handleGrantLeave}
              onDeleteLeaveRequest={handleDeleteLeaveRequest}
            />
          ))}
        </Grid>
      )}

      {/* Leave Grant Dialog */}
      <LeaveGrantDialog
        open={leaveGrantDialogOpen}
        onClose={() => setLeaveGrantDialogOpen(false)}
        employeeId={selectedEmployee?.id || null}
        employeeName={
          selectedEmployee
            ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
            : ""
        }
        onSuccess={handleLeaveGrantSuccess}
      />

      {/* Leave Allocation Dialog */}
      <Dialog
        open={showLeaveAllocationDialog}
        onClose={() => {
          setShowLeaveAllocationDialog(false);
          setLeaveAllocationMessage(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Process Annual Leave Allocation (Testing)</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will trigger the annual leave allocation process for the
            selected year. This is for testing purposes only.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Year"
            value={leaveAllocationYear}
            onChange={(e) => setLeaveAllocationYear(parseInt(e.target.value))}
            disabled={processingLeaveAllocation}
            sx={{ mb: 2 }}
          />
          {leaveAllocationMessage && (
            <Alert
              severity={
                leaveAllocationMessage.startsWith("Error") ? "error" : "success"
              }
              sx={{ mb: 2 }}
            >
              {leaveAllocationMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowLeaveAllocationDialog(false);
              setLeaveAllocationMessage(null);
            }}
            disabled={processingLeaveAllocation}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcessLeaveAllocation}
            variant="contained"
            disabled={processingLeaveAllocation}
          >
            {processingLeaveAllocation ? "Processing..." : "Process"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("leave.confirmDeleteLeaveRequest")}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {t("leave.deleteLeaveRequestWarning")}
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {deleteConfirmDialog.employeeName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {deleteConfirmDialog.leaveDetails}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Memoized employee card component to prevent unnecessary re-renders
interface EmployeeCardProps {
  employee: Employee;
  balances: LeaveBalance[];
  leaveRequests: LeaveRequest[];
  year: number;
  currentYear: number;
  theme: any;
  t: any;
  onGrantLeave: (employee: Employee) => void;
  onDeleteLeaveRequest: (
    requestId: number,
    employeeName: string,
    leaveTypeName: string,
    startDate: string,
    endDate: string,
  ) => void;
}

const EmployeeCard = React.memo(
  ({
    employee,
    balances,
    leaveRequests,
    year,
    currentYear,
    theme,
    t,
    onGrantLeave,
    onDeleteLeaveRequest,
  }: EmployeeCardProps) => {
    const [showLeaveRequests, setShowLeaveRequests] = useState(false);

    // Filter balances to show only those with allocated days > 0 or negative (over-applied)
    const activeBalances = useMemo(
      () =>
        balances.filter(
          (b) => (b.allocatedDays || 0) > 0 || (b.availableDays || 0) < 0,
        ),
      [balances],
    );

    // Sort all leave requests by start date
    const sortedLeaveRequests = useMemo(() => {
      return [...leaveRequests].sort((a, b) => {
        return (
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        );
      });
    }, [leaveRequests]);

    // Determine which requests can be deleted based on rules:
    // 1. PENDING can be deleted anytime (past or future)
    // 2. DENIED can be deleted anytime (past or future)
    // 3. CANCELLED can be deleted anytime (past or future)
    // 4. APPROVED can only be deleted if start date is in the future (not today or past)
    const canDeleteRequest = useCallback((request: LeaveRequest) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // PENDING, DENIED, and CANCELLED can be deleted anytime
      if (
        request.status === LeaveRequestStatus.PENDING ||
        request.status === LeaveRequestStatus.DENIED ||
        request.status === LeaveRequestStatus.CANCELLED
      ) {
        return true;
      }

      // APPROVED can only be deleted if start date is in the future
      if (request.status === LeaveRequestStatus.APPROVED) {
        const startDate = new Date(request.startDate);
        startDate.setHours(0, 0, 0, 0);
        return startDate > today; // Must be strictly in the future (not today)
      }

      return false;
    }, []);

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Card
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Typography variant="h6" component="div">
                {employee.firstName} {employee.lastName}
              </Typography>
              {year !== currentYear && (
                <Chip
                  label={year}
                  size="small"
                  color="default"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {employee.email}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {employee.department}
            </Typography>

            {activeBalances.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("leave.balances")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {activeBalances.map((balance) => (
                    <Box
                      key={balance.leaveTypeCode}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        p: 1,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "grey.50",
                        borderRadius: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t(`leave.leaveTypes.${balance.leaveTypeCode}`)}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.8rem",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t("leave.allocated")}:
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {balance.allocatedDays || 0}
                        </Typography>
                      </Box>
                      {balance.leaveTypeCode === "VACATION" && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "0.8rem",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {t("leave.carryOver")}:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color:
                                (balance.carriedOverDays || 0) < 0
                                  ? "error.main"
                                  : "inherit",
                            }}
                          >
                            {balance.carriedOverDays || 0}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.8rem",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t("leave.used")}:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "warning.main",
                          }}
                        >
                          {balance.usedDays || 0}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.8rem",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t("leave.available")}:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color:
                              balance.availableDays > 0
                                ? "success.main"
                                : "error.main",
                          }}
                        >
                          {balance.availableDays || 0}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t("leave.noBalances")}
              </Typography>
            )}

            {/* Leave Requests Section */}
            {sortedLeaveRequests.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => setShowLeaveRequests(!showLeaveRequests)}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t("leave.leaveRequests")} ({sortedLeaveRequests.length})
                  </Typography>
                  {showLeaveRequests ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>

                {showLeaveRequests && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                  >
                    {sortedLeaveRequests.map((request) => (
                      <Box
                        key={request.id}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          p: 1.5,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "grey.50",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.1)"
                              : "grey.200",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, mb: 0.5 }}
                            >
                              {request.leaveTypeName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {formatDate(request.startDate)} -{" "}
                              {formatDate(request.endDate)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {request.totalDays}{" "}
                              {request.totalDays === 1
                                ? t("leave.day")
                                : t("leave.days")}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 0.5,
                            }}
                          >
                            <Chip
                              label={t(`leave.status.${request.status}`)}
                              size="small"
                              color={
                                request.status === LeaveRequestStatus.APPROVED
                                  ? "success"
                                  : request.status === LeaveRequestStatus.DENIED
                                    ? "error"
                                    : request.status ===
                                        LeaveRequestStatus.PENDING
                                      ? "warning"
                                      : "default"
                              }
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                            {canDeleteRequest(request) && (
                              <Tooltip title={t("leave.deleteLeaveRequest")}>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() =>
                                    onDeleteLeaveRequest(
                                      request.id,
                                      `${employee.firstName} ${employee.lastName}`,
                                      request.leaveTypeName,
                                      request.startDate,
                                      request.endDate,
                                    )
                                  }
                                  sx={{
                                    minWidth: "auto",
                                    px: 1,
                                    py: 0.5,
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {t("common.delete")}
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </CardContent>

          <CardActions sx={{ justifyContent: "flex-end" }}>
            <Tooltip title={t("leave.grantLeave")}>
              <Button
                size="small"
                color="primary"
                startIcon={<CardGiftcardIcon />}
                onClick={() => onGrantLeave(employee)}
              >
                {t("leave.editLeaveCredits")}
              </Button>
            </Tooltip>
          </CardActions>
        </Card>
      </Grid>
    );
  },
);

EmployeeCard.displayName = "EmployeeCard";

export default EmployeeLeaveCredits;
